import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "amountInput",
    "composerModal",
    "composerCopy",
    "composerRecipient",
    "topOffNote",
    "reviewModal",
    "reviewAlias",
    "reviewTopOffNote",
    "sendingOverlay",
    "reviewAmount",
    "reviewRecipient",
    "reviewSource",
    "reviewFee",
    "status",
    "confirmButton",
    "successBox",
    "successText",
    "successLink"
  ]

  static values = {
    sourceAddressId: Number,
    sourceAddress: String,
    recipientAlias: String,
    recipientAddress: String,
    estimatedFee: String,
    memo: String,
    baseUrl: String,
    refreshPath: String,
    refreshDelayMs: Number,
    mazaIconUrl: String,
    pinballImageUrl: String,
    insertcoinsImageUrl: String,
    pigeonImageUrl: String
  }

  connect() {
    this.pendingTransaction = null
    this.defaultRecipientAlias = this.recipientAliasValue || ""
    this.defaultRecipientAddress = this.recipientAddressValue || ""
    this.defaultMemo = this.hasMemoValue ? this.memoValue || "" : ""
    this.defaultRefreshPath = this.hasRefreshPathValue ? this.refreshPathValue || "" : ""
    this.dynamicRecipientAlias = this.defaultRecipientAlias
    this.dynamicRecipientAddress = this.defaultRecipientAddress
    this.dynamicMemo = this.defaultMemo
    this.dynamicRefreshPath = this.defaultRefreshPath
    this.refreshTimer = null
    this.hideAll()
    this.renderRecipientLabels()
  }

  disconnect() {
    if (this.refreshTimer) {
      window.clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  openComposer(event) {
    event?.preventDefault()
    this.resetDynamicContext()
    this.showComposer()
  }

  openComposerFor(event) {
    event?.preventDefault()
    const dataset = event?.currentTarget?.dataset || {}
    this.dynamicRecipientAlias = dataset.recipientAlias || this.defaultRecipientAlias
    this.dynamicRecipientAddress = dataset.recipientAddress || this.defaultRecipientAddress
    this.dynamicMemo = dataset.memo || this.defaultMemo
    this.dynamicRefreshPath = dataset.refreshPath || this.defaultRefreshPath
    this.showComposer()
  }

  showComposer() {
    this.renderRecipientLabels()
    this.clearStatus()
    this.hideSuccess()
    if (this.hasComposerModalTarget) this.composerModalTarget.hidden = false
  }

  closeComposer() {
    if (this.hasComposerModalTarget) this.composerModalTarget.hidden = true
  }

  closeReview() {
    if (this.hasReviewModalTarget) this.reviewModalTarget.hidden = true
  }

  prepareReview() {
    const amount = this.normalizedAmount()
    if (!amount) {
      this.setStatus("Enter a valid MAZA amount.")
      return
    }

    const recipientAddress = this.currentRecipientAddress()
    if (!this.sourceAddressIdValue || !recipientAddress) {
      this.setStatus("MAZA address missing for sender or recipient.")
      return
    }

    this.pendingTransaction = {
      address_id: this.sourceAddressIdValue,
      send_to: recipientAddress,
      amount,
      memo: this.buildMemo()
    }

    if (this.hasReviewAmountTarget) this.reviewAmountTarget.textContent = amount
    if (this.hasReviewRecipientTarget) this.reviewRecipientTarget.textContent = recipientAddress
    if (this.hasReviewSourceTarget) this.reviewSourceTarget.textContent = this.sourceAddressValue
    if (this.hasReviewFeeTarget) this.reviewFeeTarget.textContent = this.estimatedFeeValue
    this.renderRecipientLabels()

    this.clearStatus()
    this.closeComposer()
    if (this.hasReviewModalTarget) this.reviewModalTarget.hidden = false
  }

  async confirmSend() {
    if (!this.pendingTransaction) {
      this.setStatus("Transaction draft missing.")
      return
    }

    this.toggleConfirm(false)
    this.clearStatus()
    this.showSendingOverlay()

    try {
      const createPayload = await this.postJson("/transactions.json", {
        transaction: this.pendingTransaction
      })

      const transactionId = createPayload.transaction_id
      if (!transactionId) {
        throw new Error("Transaction draft was not created.")
      }

      const sendPayload = await this.patchJson(`/transactions/${transactionId}.json`, {
        from_show: true,
        transaction: this.pendingTransaction
      })

      this.showSuccess(sendPayload)
      this.resetAmount()
      this.closeReview()
    } catch (error) {
      console.error("❌ Chewy MAZA send failed:", error)
      this.setStatus(error.message || "Unable to send MAZA right now.")
    } finally {
      this.hideSendingOverlay()
      this.toggleConfirm(true)
    }
  }

  normalizedAmount() {
    const raw = this.hasAmountInputTarget ? this.amountInputTarget.value.toString().trim() : ""
    if (!raw) return null

    const numeric = Number.parseFloat(raw)
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return null
    }

    return numeric.toFixed(5).replace(/\.?0+$/, "")
  }

  buildMemo() {
    if (this.dynamicMemo) {
      return this.dynamicMemo.toString().slice(0, 25)
    }

    if (this.hasMemoValue && this.memoValue) {
      return this.memoValue.toString().slice(0, 25)
    }

    const alias = (this.currentRecipientAlias() || "user").replace(/\s+/g, " ").trim()
    return `Chewy @${alias}`.slice(0, 25)
  }

  resetAmount() {
    if (this.hasAmountInputTarget) {
      this.amountInputTarget.value = ""
    }
  }

  showSuccess(payload) {
    if (!this.hasSuccessBoxTarget) return

    const txId = payload.tx_id || "pending"
    this.successTextTarget.textContent = `Sent ${this.pendingTransaction.amount} MAZA to @${this.currentRecipientAlias()}. TX: ${txId}`

    if (this.hasSuccessLinkTarget) {
      const href = payload.explorer_url || payload.transaction_path || "#"
      this.successLinkTarget.href = href
      this.successLinkTarget.hidden = href === "#"
    }

    this.successBoxTarget.hidden = false
    window.SiteNotices.show(this.successBoxTarget)

    const refreshPath = this.currentRefreshPath()
    if (refreshPath) {
      const delay = Math.max(3000, this.hasRefreshDelayMsValue && this.refreshDelayMsValue > 0 ? this.refreshDelayMsValue : 3000)
      if (this.refreshTimer) window.clearTimeout(this.refreshTimer)
      this.refreshTimer = window.setTimeout(() => {
        window.location.href = refreshPath
      }, delay)
    }
  }

  currentRecipientAlias() {
    return this.dynamicRecipientAlias || this.recipientAliasValue || ""
  }

  currentRecipientAddress() {
    return this.dynamicRecipientAddress || this.recipientAddressValue || ""
  }

  currentRefreshPath() {
    return this.dynamicRefreshPath || this.defaultRefreshPath || ""
  }

  resetDynamicContext() {
    this.dynamicRecipientAlias = this.defaultRecipientAlias
    this.dynamicRecipientAddress = this.defaultRecipientAddress
    this.dynamicMemo = this.defaultMemo
    this.dynamicRefreshPath = this.defaultRefreshPath
  }

  renderRecipientLabels() {
    const chicletsMode = this.currentIsChiclets()

    if (this.hasComposerCopyTarget) {
      this.composerCopyTarget.textContent = chicletsMode
        ? "Enter the amount of MAZA to convert into Chiclets at 1 MAZA / CHICLET."
        : `Enter the amount of MAZA to send to @${this.currentRecipientAlias()}.`
    }

    if (this.hasTopOffNoteTarget) {
      this.topOffNoteTarget.hidden = !chicletsMode
    }

    if (this.hasReviewTopOffNoteTarget) {
      this.reviewTopOffNoteTarget.hidden = !chicletsMode
    }

    if (this.hasComposerRecipientTarget) {
      this.composerRecipientTarget.textContent = this.currentRecipientAlias()
    }

    if (this.hasReviewAliasTarget) {
      this.reviewAliasTarget.textContent = this.currentRecipientAlias()
    }
  }

  hideSuccess() {
    if (this.hasSuccessBoxTarget) this.successBoxTarget.hidden = true
    if (this.hasSuccessLinkTarget) this.successLinkTarget.hidden = true
  }

  hideAll() {
    if (this.hasComposerModalTarget) this.composerModalTarget.hidden = true
    if (this.hasReviewModalTarget) this.reviewModalTarget.hidden = true
    if (this.hasSendingOverlayTarget) this.sendingOverlayTarget.hidden = true
    this.hideSuccess()
    this.clearStatus()
  }

  clearStatus() {
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  toggleConfirm(enabled) {
    if (!this.hasConfirmButtonTarget) return
    this.confirmButtonTarget.disabled = !enabled
    this.confirmButtonTarget.textContent = enabled ? "CONFIRM & SEND MAZA" : "SENDING..."
  }

  showSendingOverlay() {
    if (!this.hasSendingOverlayTarget) return

    this.sendingOverlayTarget.hidden = false
    const textNode = this.sendingOverlayTarget.querySelector("[data-chew-payment-frame-text]")
    const imageNode = this.sendingOverlayTarget.querySelector("[data-chew-payment-frame-image]")
    if (!textNode || !imageNode) return

    const frames = [
      { src: this.pinballImageUrlValue, text: "Charging pinball..." },
      { src: this.insertcoinsImageUrlValue, text: "Lightning handshake..." },
      { src: this.pigeonImageUrlValue, text: "Pigeon in flight..." }
    ]

    textNode.textContent = frames[0].text
    imageNode.src = frames[0].src

    this.overlayTimers = [
      window.setTimeout(() => {
        textNode.textContent = frames[1].text
        imageNode.src = frames[1].src
      }, 3000),
      window.setTimeout(() => {
        textNode.textContent = frames[2].text
        imageNode.src = frames[2].src
      }, 6000)
    ]
  }

  hideSendingOverlay() {
    if (this.overlayTimers) {
      this.overlayTimers.forEach((timer) => window.clearTimeout(timer))
      this.overlayTimers = null
    }

    if (this.hasSendingOverlayTarget) this.sendingOverlayTarget.hidden = true
  }

  async postJson(url, body) {
    return this.fetchJson(url, "POST", body)
  }

  async patchJson(url, body) {
    return this.fetchJson(url, "PATCH", body)
  }

  async fetchJson(url, method, body) {
    const response = await fetch(this.absoluteUrl(url), {
      method,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(body)
    })

    let payload = {}
    try {
      payload = await response.json()
    } catch {
      payload = {}
    }

    if (!response.ok) {
      throw new Error(payload.error || `Request failed (${response.status})`)
    }

    return payload
  }

  absoluteUrl(path) {
    if (!path) return this.baseUrlValue || window.location.origin
    if (/^https?:\/\//i.test(path)) return path

    const base = this.hasBaseUrlValue && this.baseUrlValue ? this.baseUrlValue : window.location.origin
    return `${base.replace(/\/+$/, "")}${path.startsWith("/") ? path : `/${path}`}`
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  currentIsChiclets() {
    return this.buildMemo().toUpperCase() === "CHICLETS"
  }
}
